import { TrialBalanceComponent } from '../trial-balance/trial-balance.component';
import { NOTIFICATIONS, NotificationsService } from '../../../../core/service/notifications.service';
import { takeUntil } from 'rxjs/operators';
import { FinancialStatement } from '../../models/common/FinancialStatement';
import { GeneralLedgerTransaction } from '../../models/common/GeneralLedgerTransaction';
import { GlTableComponent } from '../gl-table/gl-table.component';
import {
  ADJUSTMENTS_TABS,
  AdjustmentsTabs,
  baseFinancialStatementItems,
  CAPITAL_ACCOUNTS_TABS,
  CapitalAccountsTabs,
  CloseApplyDismissModalButtons,
  ColumnDate,
  EmitterFlags,
  EmptyStateSettings,
  FinancialAnalyticsTabs,
  FINANCIALS_ANALYTICS_TABS,
  FS_TAB, getFsTabsByUrl,
  getFsUrlForTab,
  MANAGEMENT_ENTITY_WP_TABS,
  ManagementEntityWpTabs,
  ManualAccountObjectForEmitter,
  ManualEntryObjectForEmitter,
  OwnershipEntityType,
  PERIOD_SELECT_MODAL_TEXT,
  PlAllocationEmissionObject,
  ShowFs,
  UtilityTexts,
  WORKING_PAPERS_TABS,
  WorkingPaperTabs,
} from '../../utils/common/utilityClasses';
import { AuthService } from 'src/app/domain/auth/service/auth.service';
import { CapitalAccountComponent } from '../capital-account/capital-account.component';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import {
  CreateManualEntryModalComponent,
} from '../../modals/common/create-manual-entry-modal/create-manual-entry-modal.component';
import { ModalCreateAccountComponent } from '../../modals/common/modal-create-account/modal-create-account.component';
import { FinancialStatementAccount } from '../../models/common/FinancialStatementAccount';
import { SoiService } from '../../services/soi/soi.service';
import { AssemblyComponent } from '../assembly/assembly.component';
import { FsSelectorPeriod } from '../../utils/common/FsSelectorPeriod';
import { FsScheduleOfInvestmentsComponent } from '../fs-schedule-of-investments/fs-schedule-of-investments.component';
import { FsCalculationsService } from '../../services/common/fs-calculations.service';
import { FinancialStatementService } from '../../services/common/financial-statement.service';
import { FsNavigationService } from '../../services/fs-nav/fs-navigation.service';
import { DifferenceAdjustmentObject } from '../../utils/soi/utils';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FsGettersSettersService } from '../../services/common/fs-getters-setters.service';
import { ModalButtons } from '../../../shared/component/modal-container/modal-container.component';
import { FS_URLS } from "../../module/financial-statement-routing.module";

@Component({
  selector: 'app-financial-statement',
  templateUrl: './financial-statement.component.html',
  styleUrls: ['./financial-statement.component.scss'],
})
export class FinancialStatementComponent implements OnInit, OnDestroy {

  private _destroyStream: Subject<void> = new Subject<void>();
  @ViewChild('assemblyBs') assemblyBs: AssemblyComponent;
  @ViewChild('assemblyPl') assemblyPl: AssemblyComponent;
  @ViewChild('modalCreate') modalCreate: ModalCreateAccountComponent;
  @ViewChild('glTable') glTable: GlTableComponent;
  @ViewChild('manualEntryModal') manualEntryModal: CreateManualEntryModalComponent;
  @ViewChild('manualAccountsTable') manualAccountsTable: TrialBalanceComponent;
  @ViewChild('capitalAccount') capitalAccount: CapitalAccountComponent;
  @ViewChild('SOI')soi: FsScheduleOfInvestmentsComponent;

  @Input() bsColumnsSelectedDates: string[];
  isManagementEntity: boolean;
  isAdmin: boolean;
  isShowUpload = false;
  isReset = false;
  financialStatement: FinancialStatement;
  drillDownBS = false;
  drillDownPL = false;
  show = new ShowFs();
  selectedTab: FS_TAB;
  selectedTabIndex = 0;
  fsTabs: FS_TAB[];
  adjustmentsTabs: FS_TAB[] = AdjustmentsTabs;
  selectedAdjustmentTab: FS_TAB = ADJUSTMENTS_TABS.ME;
  selectedAdjustmentTabIndex = 0;
  manualEntries: GeneralLedgerTransaction[];
  manualAccounts: FinancialStatementAccount[];
  selectedPeriod: Date;
  financialStatementYear: string;
  previousYear: string;
  manualEntryForEdit: GeneralLedgerTransaction;
  manualAccountForEdit: FinancialStatementAccount;
  periods: FsSelectorPeriod[]; // periods for selector
  datePickerDate: string;
  doesDatePickerDateExist: boolean = false;
  financialStatementPeriodsForCopy: Date[]; // periods with a statement saved not including selected period
  scrollPosition: number = null;
  baseFinancialStatementItems = baseFinancialStatementItems;
  emptyStateSettings: EmptyStateSettings;
  periodSelectModalText = PERIOD_SELECT_MODAL_TEXT;
  thinkTwiceModalText: UtilityTexts;
  showEmptyState = false;
  url: string;
  capAccUi = {
    isCapAccountManagementLocked: false,
    investorsLength: 0,
  };

  periodCreateModalButtons: ModalButtons = CloseApplyDismissModalButtons;
  constructor(
    protected fsService: FinancialStatementService,
    private notificationService: NotificationsService,
    private authService: AuthService,
    private fsNavService: FsNavigationService,
    private soiService: SoiService,
    private router: Router,
    private route: ActivatedRoute,
    protected fsGetterSetter: FsGettersSettersService,
  ) {
    // set assembly as default
    if (!FsGettersSettersService.getSavedTab()) {
      FsGettersSettersService.setSavedTab(WORKING_PAPERS_TABS.ASSEMBLY);
    }
    this.router.events.pipe(takeUntil(this._destroyStream)).subscribe((res) => {
      if (!(res instanceof NavigationEnd)) {
        return;
      }
      this.isReset = this.url?.includes(FS_URLS.base);
      this.url = res.urlAfterRedirects;
      void this.tabsHandlerWithOptionalReset();
    });
  }

  async ngOnInit(): Promise<void> {
    this.initSubscriptions();
    await this.fsService.updateFinancialStatementData(false);
  }

  ngOnDestroy(): void {
    sessionStorage.removeItem('savedFsTab');
    this.fsService.clearData();
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  setEmptyStateSettings(): void {
    if (!this.periods) {
      return;
    }
    this.emptyStateSettings = {
      text: {
        title: 'No data yet.',
        description: this.periods?.length ?
          'Change period or contact organization admin to upload or unlock data in this period.' :
          'No periods exist for the selected fund or management company.',
      },
      buttonSettings: !this.periods?.length ? {
        iconName: 'plus.svg',
        text: 'Create new period',
      } : null,

    };
    this.showEmptyState = (!this.isAdmin && !this.financialStatement) || !this.periods?.length;
  }

  setThinkTwiceModalTexts(showThinkTwiceModal?: boolean): void {
    this.thinkTwiceModalText = {
      title: 'Delete structure',
      description: this.show.thinkTwiceModalForStructure ? 'Structure will be deleted' : 'All data will be deleted',
    };
    if (showThinkTwiceModal) {
      this.show.deleteModal = showThinkTwiceModal;
    }
  }

  copyAdjustmentsSetup(): void {
    this.show.copyAdjustmentsModal = true;
  }

  saveScrollPosition(): void {
    this.scrollPosition = window.scrollY;
  }

  getScrollPosition(): void {
    window.scrollTo({ top: this.scrollPosition, behavior: 'auto' });
  }

  scrollChangeCatcher(event: boolean): void {
    event ? this.saveScrollPosition() : this.getScrollPosition();
  }

  // used to minimize either the Balance Sheet or Profit/Loss table when the user drills down in assembly
  changeDrillDown(isBalanceSheet: boolean, drillDown: boolean): void {
    if (isBalanceSheet) {
      this.drillDownBS = drillDown;
    } else {
      this.drillDownPL = drillDown;
    }
    if (drillDown) {
      this.saveScrollPosition();
    } else {
      setTimeout(() => {
        this.getScrollPosition();
      }, 175);
    }
  }

  tabsHandlerWithOptionalReset(isReset?: boolean): Promise<void> {
    if (!this.periods?.length) {
      this.setTabsNull();
      return;
    }
    this.initTabs();
    if (this.financialStatement === null) {
      return;
    }
    if (isReset) {
      this.fsTabs = WorkingPaperTabs;
      this.selectedTab = undefined;
    } else {
      this.changeTab(FsGettersSettersService.getSavedTab());
    }
    this.checkSelectedTab();
  }

  initTabs(): void {
    // check if selected entity is fund or management entity
    const isFund = this.fsService.ownershipEntityType === OwnershipEntityType.FUND;
    if (this.financialStatement) {
      // if fs exists for entity/period set appropriate respective tabs
      this.fsTabs = getFsTabsByUrl(this.url, isFund);
    } else if (this.isAdmin) {
      // if no fs for entity/period combo but is admin, set periods to only gl tab
      this.fsTabs = [WORKING_PAPERS_TABS.GL];
      this.changeTab(this.fsTabs[0]);
    } else {
      // if no fs for entity/period combo and not admin, set tabs to null
      this.setTabsNull();
    }
  }

  setTabsNull(): void {
    this.fsTabs = [];
    this.selectedTabIndex = null;
    this.selectedTab = null;
  }

  checkSelectedTab(): void {
    if (this.financialStatement) {
      if (this.fsTabs.includes(this.selectedTab)) {
        this.changeTab(this.selectedTab);
        return;
      }
      switch (this.fsTabs) {
        case (WorkingPaperTabs):
          this.changeTab(WORKING_PAPERS_TABS.ASSEMBLY);
          return;
        case (ManagementEntityWpTabs):
          this.changeTab(MANAGEMENT_ENTITY_WP_TABS.ASSEMBLY);
          return;
        case (FinancialAnalyticsTabs):
          this.changeTab(FINANCIALS_ANALYTICS_TABS.BS);
          return;
        case (CapitalAccountsTabs):
          this.changeTab(CAPITAL_ACCOUNTS_TABS.CA);
          return;
      }
    }
    this.changeTab(WORKING_PAPERS_TABS.GL);
  }

  changeTab(selectedTab: FS_TAB): void {
    if (this.selectedTab === selectedTab) {
      return;
    }
    if (this.adjustmentsTabs.includes(selectedTab)) {
      this.selectedAdjustmentTab = selectedTab;
    } else {
      this.selectedTab = selectedTab;
      this.selectedTabIndex = this.fsTabs.indexOf(selectedTab);
      this.navigateByTab(selectedTab);
      if (this.financialStatement) {
        FsGettersSettersService.setSavedTab(this.selectedTab);
      }
    }
    if (selectedTab === WORKING_PAPERS_TABS.ASSEMBLY as string) {
      [this.drillDownBS, this.drillDownPL] = [false, false];
    }
    this.fsNavService.checkCapAccountManagement();
  }

  /**
   * Updates the selected dates of the balance sheet columns to pass them to the charts, s.t. the charts are updated
   * each time a new date is selected.
   *
   * This function takes a Map of selected dates from the balance sheet columns and extracts the selected dates.
   * It then updates the `bsColumnsSelectedDates` property of this component with the extracted selected dates
   * to pass them as an input to BS charts.
   *
   * @param bsDateColumns - A Map of selected dates from the balance sheet columns.
   */
  updateBsDateColumns(bsDateColumns: Map<string, ColumnDate>): void {
    this.bsColumnsSelectedDates = [...bsDateColumns.values()]
      .map((value) => value.selectedDate)
      .filter((date) => date);
  }

  openManualEntryModal(): void {
    this.manualEntryForEdit = null;
    this.selectedPeriod = this.fsGetterSetter.selectedEntityPeriod.value.period;
    this.show.modalForManualEntry = true;
  }

  setValuesForManualEntryModal(presetData: GeneralLedgerTransaction): void {
    setTimeout(() => {
      this.manualEntryModal.creditAccount = presetData.debit !== 0 ? presetData.contraAccount : presetData.account;
      this.manualEntryModal.debitAccount = presetData.debit !== 0 ? presetData.account : presetData.contraAccount;
      this.manualEntryModal.entryAmount = presetData.debit !== 0 ? presetData.debit : presetData.credit;
      this.manualEntryModal.entryDate = presetData.transactionDate;
      this.manualEntryModal.description = presetData.description;
      this.manualEntryModal.comment = presetData.comment;
      this.manualEntryModal.updateAccountsStringsToBeShownInSelector();
      this.manualEntryModal.checkSave();
    }, 100);
  }

  manualEntryEmitCatcher(manualEntryEmitterObject: ManualEntryObjectForEmitter): void {
    switch (manualEntryEmitterObject.action) {
      case EmitterFlags.EDIT: {
        this.showModalForEditManualEntry(manualEntryEmitterObject.manualEntry);
        break;
      }
      case EmitterFlags.DUPLICATE: {
        this.openManualEntryModal();
        this.setValuesForManualEntryModal(manualEntryEmitterObject.manualEntry);
        break;
      }
      case EmitterFlags.CONVERT: {
        void this.fsService.saveManualEntry(manualEntryEmitterObject.manualEntry);
        break;
      }
      case EmitterFlags.DELETE: {
        const journalEntryCodeSet = new Set([manualEntryEmitterObject.manualEntry.journalEntryCode]);
        void this.deleteManualEntries(journalEntryCodeSet);
        break;
      }
    }
  }

  async confirmManualEntry(manualEntry: GeneralLedgerTransaction): Promise<void> {
    this.show.modalForManualEntry = false;
    if (manualEntry) {
      // if editing entries, remove old entries from old entry accounts
      if (this.manualEntryForEdit) {
        // find account from manual entry being edited and remove this entry from its list of transactions
        const fsAccount = this.fsService.financialStatement.tb.find((account) =>
          account.code === this.manualEntryForEdit.account.code);
        fsAccount.glTransactions = fsAccount.glTransactions.filter((entry) =>
          entry.journalEntryCode !== this.manualEntryForEdit.journalEntryCode);

        // find contraAccount from manual entry being edited and remove this entry from its list of transactions
        const fsContraAccount = this.fsService.financialStatement.tb.find((account) =>
          account.code === this.manualEntryForEdit.contraAccount.code);
        fsContraAccount.glTransactions = fsContraAccount.glTransactions.filter((entry) =>
          entry.journalEntryCode !== this.manualEntryForEdit.journalEntryCode);
      }
      // regardless if edit or new, save
      await this.fsService.saveManualEntry(manualEntry);
      // soi service might not exist as it only inits in soi tab but needs to be calculated if you are creating from soi
      await this.soi?.calculateSOI();
    }
  }

  showModalForEditManualEntry(manualEntryForEdit?: GeneralLedgerTransaction): void {
    this.show.modalForManualEntry = true;
    this.manualEntryForEdit = manualEntryForEdit;
  }

  /**
   * Deletes a list of manual entries by their journal entry codes as a pair of <transaction, contraTransaction>
   * share the same journal entry code.
   */
  async deleteManualEntries(journalEntryCodes: Set<string>): Promise<void> {
    const entriesToDelete = this.manualEntries.filter((entry) => journalEntryCodes.has(entry.journalEntryCode));
    await this.fsService.deleteManualEntries(entriesToDelete);
    await this.soi?.calculateSOI();
    this.assemblyBs?.calculateTotalsAndSums();
    this.assemblyPl?.calculateTotalsAndSums();
  }

  async confirmAddAccount(newAccount: FinancialStatementAccount, isUpdate: boolean): Promise<void> {
    this.show.addAccountModal = false;
    if (!newAccount) {
      return;
    }
    const savedAccount = await this.fsService.saveManualAccount(newAccount, isUpdate);
    this.manualAccounts = this.financialStatement.tb.filter((account) => account.isManual);
    if (this.assemblyBs) {
      this.assemblyBs.updateUnallocatedAccounts();
    }
    if (this.assemblyPl) {
      this.assemblyPl.updateUnallocatedAccounts();
    }
    if (this.manualAccountsTable) {
      this.manualAccountsTable.tbData = this.manualAccounts;
      this.manualAccountsTable.dataSetup();
    }

    if (this.manualEntryModal) {
      this.manualEntryModal.accountSetup();
      if (this.manualEntryModal.isManualAccountDebit) {
        this.manualEntryModal.debitAccount = savedAccount;
      } else {
        this.manualEntryModal.creditAccount = savedAccount;
      }
      this.manualEntryModal.updateAccountsStringsToBeShownInSelector();
      this.manualEntryModal.checkSave();
    }
    if (this.selectedTab === WORKING_PAPERS_TABS.SOI as string) {
      await this.soiService.calculateSOI();
    }
  }

  async manualAccountEmitCatcher(manualAccountEmitterObject: ManualAccountObjectForEmitter): Promise<void> {
    switch (manualAccountEmitterObject.action) {
      case EmitterFlags.EDIT: {
        this.editManualAccount(manualAccountEmitterObject.manualAccount);
        break;
      }
      case EmitterFlags.DELETE: {
        await this.deleteManualAccounts([manualAccountEmitterObject.manualAccount]);
        break;
      }
    }
  }

  editManualAccount(account: FinancialStatementAccount): void {
    this.manualAccountForEdit = account;
    this.show.addAccountModal = true;
  }

  async deleteManualAccounts(accounts: FinancialStatementAccount[]): Promise<void> {
    const associatedDeletedEntries = await this.fsService.deleteManualAccounts(accounts);
    if (!associatedDeletedEntries) {
      return;
    }
    const deletedAccountsIds = accounts.map((account) => account.id);
    // delete contra transactions
    const allAffectedContraTransactions = (this.manualEntries ?? []).filter(
      (transaction) =>
        associatedDeletedEntries.includes(transaction.id) &&
        deletedAccountsIds.includes(transaction.contraAccount.id),
    );
    allAffectedContraTransactions.forEach((transaction) => {
      transaction.account.glTransactions =
        transaction.account.glTransactions.filter(
          (t) => t.id !== transaction.id,
        );
      FsCalculationsService.calculateAccountsTotals([transaction.account], Number(this.financialStatementYear));
    });

    // delete from gl
    this.financialStatement.gl = this.financialStatement.gl.filter(
      (transaction) => !associatedDeletedEntries.includes(transaction.id),
    );
    // delete from manual entries
    this.fsService.manualEntries.next(this.fsService.manualEntries.value.filter((entry) =>
      !associatedDeletedEntries.includes(entry.id)));
    // delete from items
    accounts.forEach((account) => {
      account.financialStatementItems.forEach((item) => {
        item.financialStatementAccounts =
          item.financialStatementAccounts.filter((a) => a.id !== account.id);
      });
    });
    // delete from tb
    this.financialStatement.tb = this.financialStatement.tb.filter(
      (tbAccount) => !deletedAccountsIds.includes(tbAccount.id),
    );
    // delete from manual accounts
    this.manualAccounts = this.manualAccounts.filter(
      (manualAccount) => !deletedAccountsIds.includes(manualAccount.id),
    );
    this.assemblyBs?.updateUnallocatedAccounts();
    this.assemblyPl?.updateUnallocatedAccounts();
    this.assemblyBs?.calculateTotalsAndSums();
    this.assemblyPl?.calculateTotalsAndSums();
    void this.soiService.calculateSOI();
    this.notificationService.success(NOTIFICATIONS.SAVE_SUCCESS);
  }

  showModalForAddAccount(): void {
    this.manualAccountForEdit = null;
    this.show.addAccountModal = true;
  }

  clearStructure(): void {
    this.show.thinkTwiceModalForStructure = false;
    void this.fsService.clearFsStructure();
  }

  catchThinkTwiceModalEmit(isConfirmed: boolean): void {
    if (!isConfirmed) {
      this.show.deleteModal = false;
      this.show.thinkTwiceModalForStructure = false;
      this.fsGetterSetter.setShowDeleteModal(false);
      return;
    }

    if (this.show.deleteModal) {
      this.fsNavService.clearAllData();
      this.show.deleteModal = false;
      this.fsGetterSetter.setShowDeleteModal(false);
      return;
    }

    if (this.show.thinkTwiceModalForStructure) {
      this.clearStructure();
      this.show.thinkTwiceModalForStructure = false;
      this.fsGetterSetter.setShowDeleteModal(false);
    }
  }


  async catchCopyPeriod(period: Date): Promise<void> {
    if (period) {
      await this.fsService.copyFsStructure(period);
    }
    this.fsGetterSetter.setShowCopyPeriodModal(false);
  }

  threeDotMenuEmitterCatcher(event: 'copyStruct' | 'clearStruct'): void {
    if (event === 'copyStruct') {
      this.fsNavService.findPeriodsForCopy();
      this.financialStatementPeriodsForCopy = this.fsNavService.findPeriodsForCopy();
      this.show.copyStructureModal = true;
    } else if (event === 'clearStruct') {
      this.show.thinkTwiceModalForStructure = true;
      this.setThinkTwiceModalTexts();
    }
  }

  updateAccumulatedPl(event: PlAllocationEmissionObject): void {
    if (!event) {
      return;
    }
    const selectedAccounts = this.assemblyBs.financialStatement.tb
      .filter((bsAcc) => event.selectedAccounts.some((plAcc) => plAcc.code === bsAcc.code));
    const accumulatedPlItem = this.assemblyBs.financialStatement.items.find((item) =>
      item.name === this.baseFinancialStatementItems.partnersCapitalItems.slice(-1)[0]);
    if (!accumulatedPlItem) {
      return;
    }
    this.assemblyBs.editAccounts(selectedAccounts, accumulatedPlItem);
  }

  createNewPeriod(period: string): Promise<void> {
    this.closeCreatePeriodModal();
    return this.fsNavService.createPeriod(period, this.fsService.ownershipUuid);
  }

  openCreatePeriodModal(): void {
    this.fsGetterSetter.setShowCreatePeriodModal(true);
  }

  closeCreatePeriodModal(): void {
    this.fsGetterSetter.setShowCreatePeriodModal(false);
  }

  createPickerDateSelected(selectedDate: string): void {
    this.datePickerDate = selectedDate;
    this.doesDatePickerDateExist = this.periods.some((period) => String(period.period) === selectedDate);
  }

  catchAdjustmentFromSOI(event: DifferenceAdjustmentObject): void {
    if (!event) {
      return;
    }
    this.openManualEntryModal();
    setTimeout(() => {
      const account = this.manualEntryModal.accounts.find((acc) => acc.code === event.accountCode);
      if (account) {
        if (event.value < 0) {
          this.manualEntryModal.creditAccount = account;
          this.manualEntryModal.entryAmount = event.value / -1;
        } else {
          this.manualEntryModal.debitAccount = account;
          this.manualEntryModal.entryAmount = event.value;
        }
        this.manualEntryModal.updateAccountsStringsToBeShownInSelector();
      } else {
        this.manualEntryModal.entryAmount = event.value < 0 ? event.value / -1 : event.value;
      }
    }, 100);
  }

  private initSubscriptions(): void {
    this.subscribeToUser();
    this.subscribeToFsData();
    this.subscribeToFsNav();
    this.subscribeToUiVars();
  }

  private subscribeToUser(): void {
    this.authService.currentUserTypeForServices$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((userType) => {
        this.isAdmin = userType === 'admin';
        void this.tabsHandlerWithOptionalReset();
        this.setEmptyStateSettings();
      });
  }

  private subscribeToFsNav(): void {
    this.fsGetterSetter.ownershipEntityPeriods
      .pipe(takeUntil(this._destroyStream))
      .subscribe((periods) => {
        this.periods = periods;
        if (!this.periods?.length) {
          this.setTabsNull();
          this.setEmptyStateSettings();
          return;
        }
        // void this.tabsHandlerWithOptionalReset();
        this.setEmptyStateSettings();
        this.financialStatementYear = String(this.fsService.financialStatementYear);
        this.previousYear = String(Number(this.financialStatementYear) - 1);
      });
    this.fsGetterSetter.selectedEntityPeriod
      .pipe(takeUntil(this._destroyStream))
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      .subscribe(async (selectedPeriod): Promise<void> => {
        if (this.selectedPeriod === selectedPeriod?.period) {
          return;
        }
        this.selectedPeriod = selectedPeriod?.period;
        this.fsService.setSelectedPeriod(selectedPeriod?.period);
        await this.fsService.updateFinancialStatementData(false);
        void this.tabsHandlerWithOptionalReset(this.isReset);
      });
    this.fsGetterSetter.selectedOwnershipEntity
      .pipe(takeUntil(this._destroyStream))
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      .subscribe(async (selectedEntity): Promise<void> => {
        this.fsService.setOwnershipEntity(selectedEntity);
        await this.fsService.updateFinancialStatementData(false);
        this.isManagementEntity = this.fsService.ownershipEntityType === OwnershipEntityType.MANAGEMENT_ENTITY;
        void this.tabsHandlerWithOptionalReset(this.isReset);
      });
  }

  private subscribeToFsData(): void {
    this.fsGetterSetter.financialStatement
      .pipe(takeUntil(this._destroyStream))
      .subscribe((financialStatement) => {
        if (financialStatement === undefined) {
          return;
        }
        this.financialStatement = financialStatement; // set new fs
        if (!this.financialStatement) { // check new fs
          this.setEmptyStateSettings();
          void this.tabsHandlerWithOptionalReset();
          this.isShowUpload = true;
          return;
        }
        this.isShowUpload = false;
        this.manualAccounts = this.financialStatement.tb.filter((account) => account.isManual === true);
        this.setEmptyStateSettings();
        void this.tabsHandlerWithOptionalReset();
        if (this.selectedTab === WORKING_PAPERS_TABS.SOI as string) {
          void this.soiService.calculateSOI();
        }
        this.capAccUi.investorsLength = this.financialStatement.investors.length;
      });
    this.fsService.manualEntries
      .pipe(takeUntil(this._destroyStream))
      .subscribe((manualEntries) => {
        if (!this.financialStatement) {
          return;
        }
        this.manualEntries = manualEntries;
        FsCalculationsService.calculateBalanceSheetTotals(
          this.financialStatement.balanceSheetNetTotals, this.financialStatement.balanceSheetParts, false,
        );
        FsCalculationsService.calculateBalanceSheetTotals(
          this.financialStatement.profitLossNetTotals, this.financialStatement.profitLossParts, true,
        );
      });
  }

  private subscribeToUiVars(): void {
    this.fsGetterSetter.showCopyPeriodModal
      .pipe(takeUntil(this._destroyStream))
      .subscribe((showCopyPeriodModal) => {
        this.show.copyStructureModal = showCopyPeriodModal;
        if (showCopyPeriodModal) {
          this.financialStatementPeriodsForCopy = this.fsNavService.findPeriodsForCopy();
        }
      });
    this.fsGetterSetter.showCreatePeriodModal
      .pipe(takeUntil(this._destroyStream))
      .subscribe((showCreatePeriodModal) => {
        this.show.createPeriodModal = showCreatePeriodModal;
      });
    this.fsGetterSetter.showDeleteModal
      .pipe(takeUntil(this._destroyStream))
      .subscribe((showDeleteModal) => {
        this.setThinkTwiceModalTexts(showDeleteModal);
      });
    this.fsGetterSetter.isCapAccountManagementLocked
      .pipe(takeUntil(this._destroyStream))
      .subscribe((capAccountLock) => {
        this.capAccUi.isCapAccountManagementLocked = capAccountLock;
      });
  }

  private async navigateByTab(tab: FS_TAB): Promise<void> {
    if (!this.router.url.includes(FS_URLS.base)) {
      return;
    }
    const tabAsUrl = getFsUrlForTab(tab);
    const newUrl = `${FS_URLS.base}/${tabAsUrl}`;
    await this.router.navigate([newUrl], {
      queryParams: this.route.snapshot.queryParams,
    });
  }

}
